import { customGetAPI } from "services/utils/CustomAPI";
import { defaultAllSelection } from "services/constants";

const toIsoDate = (d) => d.toISOString().split("T")[0];

// customGetAPI wraps every raw response body under `responseDetails` - most
// of these endpoints return a bare array there, but Advance Salary's wraps
// the array again under `.data`. This helper normalizes both shapes so each
// entry below doesn't have to repeat the unwrap logic.
const rowsFromResponse = (res) => {
  const body = res?.responseDetails;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

const pendingRows = async (url) => {
  const res = await customGetAPI(url);
  return rowsFromResponse(res).filter((r) => r?.status === "Pending");
};

// Each entry describes one item TYPE the Action Centre aggregates. The
// Centre reads from this list — it does not own any item's data or its
// resolution; every item belongs to the module that raised it (here,
// Leave belongs to Workforce/My Zone Apply, same as Home's
// MyPendingRequestsSection). Adding a future type like a missing-punch
// correction means adding another entry here, not touching the strip
// or drawer components.
//
// P1 scope: fetchPendingItems is read-only. There is no "complete"/
// "originate" action wired to any entry yet - that arrives per item type
// in P2, reusing each type's own existing submission/approval flow
// (e.g. Leave's create/leave-request path) rather than a drawer-local
// write path.
//
// `approval` (optional): the same shape's data source for the OTHER
// side — "am I an approver, and what's pending for me to approve".
// `screen` is the exact submodule name checkEditorPermission(...,
// "Approve", screen) expects for that type (confirmed per type against
// the live Team Zone > Approve screen that already gates on it — see
// QW39B_WIDEN_ACTION_CENTRE_AND_HOME_CARDS report for the one exception,
// Advance Salary, where no such gate exists on the live screen today).
// Home's MyPendingRequestsSection/PendingApprovalsSection consume both
// halves of this same registry rather than each keeping their own
// hardcoded Leave-only fetch — see that report for why.
export const actionCentreRegistry = [
  {
    type: "leave",
    owningModule: "Workforce",
    itemLabel: "Leave request",
    viewAllPath: "/admin/myzone/apply?view=leave",
    // Same endpoint, window and status filter as
    // Home/MyPendingRequestsSection.js - both surface the employee's own
    // pending Leave requests.
    fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 30);
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 90);
      const res = await customGetAPI(
        `${domain}leavesummary/?user_id=${userDetails?.id}&start_date=${toIsoDate(
          startDate,
        )}&end_date=${toIsoDate(endDate)}&company_id=${topbarCompanyId}`,
      );
      const requests =
        res?.responseDetails?.leave_count?.leave_requests || [];
      return requests
        .filter((r) => r?.status === "Pending")
        .map((r, idx) => ({
          id: r.id ?? idx,
          title: r.leave_type || "Leave",
          subtitle: `${r.from_date} - ${r.to_date}`,
          status: r.status,
        }));
    },
    approval: {
      screen: "Leaves",
      viewAllPath: "/admin/teamzone/approve?view=leave",
      // Same endpoint/window as the pre-existing PendingApprovalsSection.
      fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        const endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 90);
        const res = await customGetAPI(
          `${domain}leave-request/?user_id=${userDetails?.id}&department_id=${defaultAllSelection}&selected_user_id=${defaultAllSelection}&start_date=${toIsoDate(
            startDate,
          )}&end_date=${toIsoDate(endDate)}&company_id=${topbarCompanyId}`,
        );
        const requests = res?.responseDetails || [];
        return requests
          .filter((r) => r?.status === "Pending")
          .map((r, idx) => ({
            id: r.id ?? idx,
            title: r.username || r.leave_type || "Leave request",
            subtitle: `${r.from_date} - ${r.to_date}`,
            status: r.status,
          }));
      },
    },
  },
  {
    // Overtime has a pre-/post-approval two-stage shape (unlike Leave's
    // single status): a request first goes through Pre-Approval (should
    // the OT even be worked), then separately through Post-Approval (was
    // it actually worked, how much gets paid out). Both stages use their
    // own "Pending" status independently, so "pending" for aggregation
    // purposes means BOTH stages' Pending rows, tagged by which stage
    // they're in - not just one or the other, since either stage can be
    // the thing genuinely awaiting action.
    type: "overtime",
    owningModule: "Workforce",
    itemLabel: "Overtime request",
    viewAllPath: "/admin/myzone/apply?view=overtime",
    fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
      const stages = [
        {
          label: "Pre-Approval",
          url: `${domain}My_Zone/Apply/Over_time/ot-pre-approval/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
          dateKey: "ot_date",
        },
        {
          label: "Post-Approval",
          url: `${domain}My_Zone/Apply/Over_time/ot-post-approval/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
          dateKey: "date",
        },
      ];
      const results = await Promise.all(
        stages.map(async (stage) => {
          const rows = await pendingRows(stage.url);
          return rows.map((r, idx) => ({
            id: `${stage.label}-${r.id ?? idx}`,
            title: `Overtime (${stage.label})`,
            subtitle: r[stage.dateKey] || "",
            status: r.status,
          }));
        }),
      );
      return results.flat();
    },
    approval: {
      screen: "Overtime",
      viewAllPath: "/admin/teamzone/approve?view=overtime",
      fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
        const stages = [
          {
            label: "Pre-Approval",
            url: `${domain}Team_Zone/Approve/Over_time/ot-pre-approve/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
            dateKey: "ot_date",
          },
          {
            label: "Post-Approval",
            url: `${domain}Team_Zone/Approve/Over_time/ot-post-approve/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
            dateKey: "date",
          },
        ];
        const results = await Promise.all(
          stages.map(async (stage) => {
            const rows = await pendingRows(stage.url);
            return rows.map((r, idx) => ({
              id: `${stage.label}-${r.id ?? idx}`,
              title: r.employee_name || `Overtime (${stage.label})`,
              subtitle: r[stage.dateKey] || "",
              status: r.status,
            }));
          }),
        );
        return results.flat();
      },
    },
  },
  {
    // Off-Day Duty has the same pre-/post-approval two-stage shape as
    // Overtime, for the same reason (approval to work the off-day, then
    // approval of what was actually worked) - both stages' Pending rows
    // are aggregated here.
    type: "offDayDuty",
    owningModule: "Workforce",
    itemLabel: "Off-Day Duty request",
    viewAllPath: "/admin/myzone/apply?view=offday-duty",
    fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
      const stages = [
        {
          label: "Pre-Approval",
          url: `${domain}my_zone/apply/day_off/myzone-pre-day-off/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
        },
        {
          label: "Post-Approval",
          url: `${domain}my_zone/apply/day_off/myzone-post-day-off/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
        },
      ];
      const results = await Promise.all(
        stages.map(async (stage) => {
          const rows = await pendingRows(stage.url);
          return rows.map((r, idx) => ({
            id: `${stage.label}-${r.id ?? idx}`,
            title: `Off-Day Duty (${stage.label})`,
            subtitle: r.date || "",
            status: r.status,
          }));
        }),
      );
      return results.flat();
    },
    approval: {
      screen: "Off Day Duty",
      viewAllPath: "/admin/teamzone/approve?view=offday-duty",
      fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
        const stages = [
          {
            label: "Pre-Approval",
            url: `${domain}team_zone/approve/day_off/teamzone-pre-day-off/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
          },
          {
            label: "Post-Approval",
            url: `${domain}team_zone/approve/day_off/teamzone-post-day-off?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
          },
        ];
        const results = await Promise.all(
          stages.map(async (stage) => {
            const rows = await pendingRows(stage.url);
            return rows.map((r, idx) => ({
              id: `${stage.label}-${r.id ?? idx}`,
              title: r.employee_name || `Off-Day Duty (${stage.label})`,
              subtitle: r.date || "",
              status: r.status,
            }));
          }),
        );
        return results.flat();
      },
    },
  },
  {
    // Work Adjustment is single-status like Leave (Pending/Approved/
    // Rejected) - no separate pre-/post-approval stage exists for it.
    type: "workAdjustment",
    owningModule: "Workforce",
    itemLabel: "Work Adjustment request",
    viewAllPath: "/admin/myzone/apply?view=work-adjustment",
    fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
      const rows = await pendingRows(
        `${domain}my_zone/apply/work_adjustment/pre-special-attendance/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
      );
      return rows.map((r, idx) => ({
        id: r.id ?? idx,
        title: r.attendance_type || "Work Adjustment",
        subtitle: r.date || "",
        status: r.status,
      }));
    },
    approval: {
      screen: "WorkAdjustment",
      viewAllPath: "/admin/teamzone/approve?view=work-adjustment",
      fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
        const rows = await pendingRows(
          `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
        );
        return rows.map((r, idx) => ({
          id: r.id ?? idx,
          title: r.employee_name || r.attendance_type || "Work Adjustment",
          subtitle: r.date || "",
          status: r.status,
        }));
      },
    },
  },
  {
    // Attendance Correction is its own request type (own "Attendance
    // Correction" submodule), not part of Work Adjustment -- without this
    // entry Home had no row for it at all. Single-status like Leave.
    type: "attendanceCorrection",
    owningModule: "Workforce",
    itemLabel: "Attendance Correction request",
    viewAllPath: "/admin/myzone/apply?view=attendance-correction",
    fetchPendingItems: async ({ domain, userDetails }) => {
      const rows = await pendingRows(
        `${domain}my_zone/apply/attendance_correction/myzone-attendance-correction/?user_id=${userDetails?.id}&status=Pending`,
      );
      return rows.map((r, idx) => ({
        id: `${r.user_id}-${r.raw_date || r.date || idx}`,
        title: "Attendance Correction",
        subtitle: r.date || "",
        status: r.status,
      }));
    },
    approval: {
      screen: "Attendance Correction",
      viewAllPath: "/admin/teamzone/approve?view=attendance-correction",
      fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        const rows = await pendingRows(
          `${domain}team_zone/approve/attendance_correction/teamzone-attendance-correction/?user_id=${userDetails?.id}&company_id=${topbarCompanyId || 99999}&start_date=${toIsoDate(startDate)}&end_date=${toIsoDate(today)}&status=Pending`,
        );
        return rows.map((r, idx) => ({
          id: `${r.user_id}-${r.raw_date || r.date || idx}`,
          title: r.employee_name || "Attendance Correction",
          subtitle: r.date || "",
          status: r.status,
        }));
      },
    },
  },
  {
    // Advance Salary is also single-status for the "is a request pending"
    // question (its post-approval lifecycle - Active/Partially-Paid/
    // Pre-Closed/Closed - only starts once it leaves Pending). Its list
    // endpoint wraps rows under `.data`, unlike every other type here -
    // handled by rowsFromResponse() above rather than a special case in
    // this entry.
    type: "advanceSalary",
    owningModule: "Workforce",
    itemLabel: "Advance Salary request",
    viewAllPath: "/admin/myzone/apply?view=advance-salary",
    fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
      const rows = await pendingRows(
        `${domain}advance/request/?selected_user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
      );
      return rows.map((r, idx) => ({
        id: r.id ?? idx,
        title: "Advance Salary",
        subtitle: r.request_date || "",
        status: r.status,
      }));
    },
    approval: {
      // NOTE: unlike every other type here, the live Team Zone > Approve >
      // Advance Salary screen (TeamZone/Approve/AdvanceSalary/index.js)
      // does not call checkEditorPermission at all - it hardcodes
      // screenAccess={true}. "Advance Salary" is used here as the
      // submodule name for consistency with the Approve-side menu entry's
      // `key` and every other type's naming convention, so this card is
      // gated at least as strictly as the rest of the app rather than
      // matching the live screen's (wider) exposure. See the report for
      // why this wasn't "fixed" on the live screen - out of scope, no
      // permission check may be touched in this pass.
      screen: "Advance Salary",
      viewAllPath: "/admin/teamzone/approve?view=advance-salary",
      fetchPendingItems: async ({ domain, userDetails, topbarCompanyId }) => {
        const rows = await pendingRows(
          `${domain}advance/request/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
        );
        return rows.map((r, idx) => ({
          id: r.id ?? idx,
          title: r.employee_name || "Advance Salary",
          subtitle: r.request_date || "",
          status: r.status,
        }));
      },
    },
  },
];
